import { AlertTriangleIcon, XIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'

/**
 * Faixa de avisos das operações que mudam a VPC de um recurso — mover VM
 * (issue #727) e excluir VPC migrando o que estava nela (issue #804).
 *
 * A operação pode terminar com sucesso E com consequência: a VPC de destino não
 * tem Cloud Gateway (a VM fica sem DHCP e sem internet), a VM trocou de endereço
 * fixo, ou o Proxmox aceitou a troca mas só a aplica no próximo boot. Antes
 * esses textos seguravam o modal aberto; aqui eles ficam ao lado da topologia já
 * atualizada.
 *
 * Trocar um overlay centralizado por conteúdo no fluxo da página tem um custo:
 * o modal era visível por construção, a faixa não. Por isso ela rola até si e
 * puxa o foco ao aparecer — o foco estava no botão "Mover para VPC", que sai do
 * DOM junto com o modal, e um `role="status"` inserido já com o conteúdo dentro
 * costuma não ser anunciado por leitor de tela.
 */

type MoveVpcWarningsBannerProps = {
  /**
   * Quem sofreu a operação — "web-01" ao mover uma VM, "2 VM(s)" ao excluir a
   * VPC onde elas estavam. A topologia tem vários recursos e o aviso fala de
   * alguns: sem o sujeito, o cliente não sabe sobre o que ele recai.
   */
  sujeito: string
  /** Nome da VPC de destino, como a API confirmou. */
  vpcName: string
  warnings: string[]
  onDismiss: () => void
}

export default function MoveVpcWarningsBanner({
  sujeito,
  vpcName,
  warnings,
  onDismiss,
}: MoveVpcWarningsBannerProps) {
  const faixaRef = useRef<HTMLDivElement>(null)

  // Dependência pelo CONTEÚDO — e o conteúdo inclui o SUJEITO. `[warnings]`
  // faria a página rolar a cada render se algum call site passasse o array
  // inline (esta página re-renderiza sozinha: refetch de 60s, reactflow), e a
  // chave só com o texto dos avisos ficaria muda no caso mais provável de
  // repetição: o aviso do backend nomeia a VPC, não a VM, então mover duas VMs
  // para a mesma VPC sem gateway gera textos idênticos.
  const chaveDosAvisos = [sujeito, vpcName, ...warnings].join('\n')
  useEffect(() => {
    if (warnings.length === 0) return
    faixaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // `preventScroll`: o `focus()` rola sozinho, na hora, e cancelaria o
    // `behavior: 'smooth'` pedido na linha acima — o efeito vira um salto.
    faixaRef.current?.focus({ preventScroll: true })
  }, [chaveDosAvisos])

  // Sem aviso não há faixa: o move limpo não merece ruído na tela.
  if (warnings.length === 0) return null

  return (
    <div
      ref={faixaRef}
      role="status"
      tabIndex={-1}
      className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 focus:ring-2 focus:ring-amber-400 focus:outline-none"
    >
      <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-600" />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium text-amber-800">
          {/* Título NEUTRO: nem todo aviso significa move em vigor — "ficou
              pendente no Proxmox" e "não foi possível confirmar" dizem o
              contrário, e um "concluída" em negrito acima deles se contradiz.
              Quem afirma o desfecho é o texto do backend; o título só dá o
              sujeito, que é o que falta lá: qual VM, para qual VPC. */}
          {['Atenção', sujeito, vpcName && `VPC ${vpcName}`].filter(Boolean).join(' — ')}
        </p>
        {warnings.map((aviso) => (
          <p key={aviso} className="text-sm text-amber-700">
            • {aviso}
          </p>
        ))}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dispensar aviso"
        className="shrink-0 text-amber-500 hover:text-amber-700"
      >
        <XIcon className="size-4" />
      </button>
    </div>
  )
}
